/**
 * Mobile bridge utility for React Native WebView communication
 *
 * This utility provides a way to communicate with the native mobile app
 * when running inside a React Native WebView. It's a noop when running
 * in a normal web browser.
 */

/**
 * Check if we're running inside a React Native WebView
 * @returns {boolean}
 */
export function isReactNativeWebView() {
  return (
    typeof window !== 'undefined' &&
    typeof window.ReactNativeWebView !== 'undefined'
  );
}

const MOBILE_MODE_STORAGE_KEY = 'auth_mobile_mode';

/**
 * Check if we're in mobile mode (via query param or localStorage)
 * @returns {boolean}
 */
export function isMobileMode() {
  if (typeof window === 'undefined') {
    return false;
  }
  const searchParams = new URLSearchParams(window.location.search);
  const urlMobileParam = searchParams.get('mobile');
  const urlMobileMode = urlMobileParam === '1';

  // If mobile=1 is in URL, persist it to localStorage
  if (urlMobileMode) {
    try {
      localStorage.setItem(MOBILE_MODE_STORAGE_KEY, '1');
    } catch (error) {
      // Ignore localStorage errors
    }
    return true;
  }

  // If mobile=0 is explicitly set, clear the stored flag
  if (urlMobileParam === '0') {
    try {
      localStorage.removeItem(MOBILE_MODE_STORAGE_KEY);
    } catch (error) {
      // Ignore localStorage errors
    }
    return false;
  }

  // Fallback to localStorage if URL doesn't have the parameter
  // This helps preserve mobile mode after redirects that don't include the parameter
  try {
    const stored = localStorage.getItem(MOBILE_MODE_STORAGE_KEY);
    return stored === '1';
  } catch (error) {
    return false;
  }
}

/**
 * Post a message to the native app
 * @param {string} type - Message type (e.g., 'LOGIN_SUCCESS', 'REGISTER_SUCCESS')
 * @param {object} payload - Message payload
 */
export function postToNative(type, payload = {}) {
  console.log('postToNative', type, payload);
  if (!isReactNativeWebView()) {
    // Noop in normal web browser
    return;
  }

  try {
    const message = JSON.stringify({ type, payload });
    window.ReactNativeWebView.postMessage(message);
  } catch (error) {
    console.error('Failed to post message to native app:', error);
  }
}
